import '@bprogress/core/css'

import { Suspense, useEffect } from 'react'
import { Outlet } from 'react-router'
import { Layout } from 'antd'
import { BProgress } from '@bprogress/core'

import B2bLoadingFullPage from '@/components/B2bLoadingFullPage'
import BackToTopButton from '@/components/BackToTopButton'
import {
  InProgressingUserModal,
  MissingDataUserModal,
  UnauthorizedUserModal,
} from '@/components/ConfigurationModal'
import Footer from '@/components/Layout/Footer'
import Header from '@/components/Layout/Header'
import MainContent from '@/components/Layout/MainContent'
import Sidebar from '@/components/Layout/Sidebar'
import SapLogViewer from '@/components/SapLogViewer'
import ScrollToTop from '@/components/ScrollToTop'
import { useAppSelector } from '@/store/hook'
import { selectDateFormat } from '@/store/selectors/configSelector'
import { selectUserStatusLowerCase } from '@/store/selectors/userSelector'
import { selectUserId } from '@/store/slices/authenticationSlice'
import {
  selectIsFetchingSetupData,
  selectShouldShowMissingDataUserModal,
} from '@/store/slices/setupSlice'
import { PunchoutProvider } from '@/contexts/punchout/PunchoutProvider'
import { SearchProvider } from '@/contexts/search/SearchProvider'
import { USER_STATUS } from '@/libs/constants/user'

const LAYOUT_STYLE = {
  backgroundColor: 'transparent',
  minHeight: 365,
}

const Loading = () => {
  useEffect(() => {
    BProgress.start()
    return () => {
      BProgress.done()
    }
  }, [])
  return null
}

const UserStatusGate = ({ children }: { children: React.ReactNode }) => {
  const userStatus = useAppSelector(selectUserStatusLowerCase)
  const isUserMissingDataFromSAP = useAppSelector(
    selectShouldShowMissingDataUserModal,
  )

  if (isUserMissingDataFromSAP) {
    return <MissingDataUserModal open />
  }

  if (userStatus === USER_STATUS.UNAUTHORIZED) {
    return <UnauthorizedUserModal open />
  }

  if (userStatus === USER_STATUS.IN_PROGRESS) {
    return <InProgressingUserModal open />
  }

  return <>{children}</>
}

const SetupLoadingGate = ({ children }: { children: React.ReactNode }) => {
  const dateFormat = useAppSelector(selectDateFormat)
  const isFetchingSetupData = useAppSelector(selectIsFetchingSetupData)
  const userId = useAppSelector(selectUserId)
  const isLoading = userId && !dateFormat

  if (isLoading || isFetchingSetupData) {
    return <B2bLoadingFullPage />
  }

  return <>{children}</>
}

const PrivateLayout = (): React.JSX.Element => (
  <SetupLoadingGate>
    <UserStatusGate>
      <PunchoutProvider>
        <SearchProvider>
          <ScrollToTop />
          <Header />
          <Layout style={LAYOUT_STYLE}>
            <Sidebar />
            <MainContent>
              <Suspense fallback={<Loading />}>
                <Outlet />
                <SapLogViewer />
              </Suspense>
            </MainContent>
          </Layout>
          <BackToTopButton />
          <Footer />
        </SearchProvider>
      </PunchoutProvider>
    </UserStatusGate>
  </SetupLoadingGate>
)

export default PrivateLayout
